import { useRetainedState } from "@appretio/shared";
import type {
  CellValueChangedEvent,
  GridApi,
  RowDataUpdatedEvent,
  SelectionChangedEvent
} from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  cubeHoldsRowPredicate,
  openFromForecastBoundary
} from "../../components/cubeDataTable/utils/columnUtils";
import { enrichDerivedRows } from "../../components/cubeDataTable/utils/derivedUtils";
import type {
  ForecastVersionSelections,
  SaveCubeKey
} from "../../components/forecastContextSelector/ForecastContextSelector";
import { useContextViewState } from "../../contextViews/useContextViewState";
import { useViewState } from "../../savedViews/useViewState";
import { isGridSaveable } from "../../utils/isGridSaveable";
import {
  pageForProducer,
  pageScope,
  useCubeSelectionState,
  usePruneStaleContextSelections
} from "../useCubeSelectionState";
import {
  type CubeProducer,
  type ForecastWorkspace,
  useForecastWorkspace
} from "../useForecastWorkspace";
import { useOpenOnMainCube } from "../useOpenOnMainCube";
import {
  useMainPageContext,
  useMainPageContextSync
} from "./useMainPageContext";
import { useMainPageVersions } from "./useMainPageVersions";

/** Configuration for the shared main page state hook. */
export interface UseMainPageStateParams {
  /** Which producer this page operates on ("user" for forecast, "budget" for budget). */
  producer: CubeProducer;
  /**
   * Which version key holds the writable scalar cube id. Narrowed to
   * {@link SaveCubeKey} — ``"user_version" | "budget_version"`` — so
   * ``selectedVersionOptions[saveCubeKey]`` resolves to ``string |
   * undefined`` without a cast, excluding the ``reference_users``
   * array and the read-only ``forecast_version`` slot.
   */
  saveCubeKey: SaveCubeKey;
}

/**
 * Selected-row state kept in sync with the grid. The graph derives its
 * series from these rows, so a selection change alone isn't enough:
 * cell edits mutate grid row data without changing the selection, so
 * the rows are also refreshed on the edit itself (instant feedback) and
 * on ``rowDataUpdated`` (fired by the mutation queue's
 * ``applyTransaction`` with backend-recalculated rows).
 */
function useSelectedRowsSync(
  queueCellChange: (event: CellValueChangedEvent) => void
) {
  const [selectedRows, setSelectedRows] = useState<Record<string, unknown>[]>(
    []
  );

  const refreshSelectedRows = useCallback((api: GridApi) => {
    setSelectedRows(enrichDerivedRows(api.getSelectedRows(), api));
  }, []);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => refreshSelectedRows(event.api),
    [refreshSelectedRows]
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent) => {
      refreshSelectedRows(event.api);
      queueCellChange(event);
    },
    [refreshSelectedRows, queueCellChange]
  );

  const handleRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent) => refreshSelectedRows(event.api),
    [refreshSelectedRows]
  );

  return {
    selectedRows,
    handleSelectionChanged,
    handleCellValueChanged,
    handleRowDataUpdated
  };
}

/**
 * Drops selected versions the fetched lists no longer offer — a cube
 * deleted while this page was unmounted, its selection retained for the
 * session. Without this the dead id stays in ``selectedVersionOptions``
 * (the trigger renders empty, because no option matches) and rides into the
 * next Apply as a cube the server cannot resolve.
 *
 * Left alone while the lists are in flight, so a refetch never wipes a
 * valid selection. Budget versions have their own guard below: they are
 * brand-scoped, so "not offered" there means "another brand's".
 */
function useClearStaleVersions(
  versions: Pick<ReturnType<typeof useMainPageVersions>, "versionsData">,
  versionState: [
    ForecastVersionSelections,
    Dispatch<SetStateAction<ForecastVersionSelections>>
  ]
) {
  const { versionsData } = versions;
  const [selectedVersionOptions, setSelectedVersionOptions] = versionState;
  useEffect(() => {
    if (!versionsData) return;
    const own = new Set(
      (versionsData.user_versions ?? []).map((version) => version.cube_id)
    );
    const references = new Set([
      ...own,
      ...(versionsData.other_user_versions ?? []).map(
        (version) => version.cube_id
      )
    ]);
    const forecasts = new Set(
      (versionsData.ds_versions ?? []).map((version) => version.cube_id)
    );
    setSelectedVersionOptions((prev) => {
      const next = { ...prev };
      if (next.user_version && !own.has(next.user_version)) {
        next.user_version = undefined;
      }
      if (next.forecast_version && !forecasts.has(next.forecast_version)) {
        next.forecast_version = undefined;
      }
      const keptReferences = (next.reference_users ?? []).filter((id) =>
        references.has(id)
      );
      if (keptReferences.length !== (next.reference_users ?? []).length) {
        next.reference_users = keptReferences;
      }
      const changed =
        next.user_version !== prev.user_version ||
        next.forecast_version !== prev.forecast_version ||
        next.reference_users !== prev.reference_users;
      return changed ? next : prev;
    });
    // The selection is a trigger, not just an input: the lists are cached
    // (``staleTime: Infinity``), so a retained selection comes back with the
    // page long after the data landed. The updater returns ``prev`` when it
    // prunes nothing, so React bails out rather than looping.
  }, [versionsData, selectedVersionOptions, setSelectedVersionOptions]);
}

/**
 * Drops a selected budget version once it's provably stale — budget
 * cubes are brand-scoped, so one the current brand's list doesn't
 * offer belongs to another brand. Validated against the fetched list
 * rather than diffing the brand: a context view swaps the brand in one
 * write, and a cube can be deleted while this page is unmounted; neither
 * is a diff this page could see. Left alone while the list is in flight
 * so a refetch doesn't wipe a valid selection.
 */
function useClearStaleBudgetVersion(
  versions: Pick<
    ReturnType<typeof useMainPageVersions>,
    "selectedBrand" | "budgetVersions"
  >,
  setSelectedVersionOptions: Dispatch<SetStateAction<ForecastVersionSelections>>
) {
  const { selectedBrand, budgetVersions } = versions;
  useEffect(() => {
    // No single brand selected means no budget cube can be valid, so
    // there's no list to wait for.
    const offered = selectedBrand ? budgetVersions?.budget_versions : [];
    if (!offered) return;
    setSelectedVersionOptions((prev) =>
      prev.budget_version &&
      !offered.some((v) => v.cube_id === prev.budget_version)
        ? { ...prev, budget_version: undefined }
        : prev
    );
  }, [selectedBrand, budgetVersions, setSelectedVersionOptions]);
}

/**
 * The page's Views layer. Forecast and budget keep separate view lists:
 * same controls, but two workspaces, and a name is unique per (user, grid).
 */
function usePageViews({
  producer,
  gridRef,
  workspace
}: {
  producer: CubeProducer;
  gridRef: RefObject<AgGridReact | null>;
  workspace: ReturnType<typeof useForecastWorkspace>;
}) {
  return useViewState({
    grid: pageForProducer(producer),
    gridStateRetention: workspace.gridStateRetention,
    tableControls: workspace.tableControls,
    setTableControls: workspace.setTableControls,
    tableControlOptions: workspace.tableControlOptions,
    setTableControlOptions: workspace.setTableControlOptions,
    metaData: workspace.metaData,
    gridRef
  });
}

/** The forecast page opens on the main cube as its user version; budget opens blank. */
function useOpenForecastOnMainCube(
  producer: CubeProducer,
  versions: Pick<ReturnType<typeof useMainPageVersions>, "versionsData">,
  setSelectedVersionOptions: Dispatch<SetStateAction<ForecastVersionSelections>>
) {
  const selectMainCube = useCallback(
    (cubeId: string) =>
      setSelectedVersionOptions((prev) => ({ ...prev, user_version: cubeId })),
    [setSelectedVersionOptions]
  );
  useOpenOnMainCube(
    pageForProducer(producer),
    producer === "user",
    versions.versionsData,
    selectMainCube
  );
}

/**
 * Whether the grid's cells are read-only, and the producer they are written
 * for when they are not.
 *
 * Live for the applied cube — a freeze or unfreeze has to reach the grid
 * without an Apply — and the Apply-time snapshot only once the versions
 * payload has stopped listing that cube.
 */
function resolveGridEditability(
  workspace: ForecastWorkspace,
  versions: Pick<ReturnType<typeof useMainPageVersions>, "isCubeFrozen">,
  producer: CubeProducer
): { isGridFrozen: boolean; editableProducer: CubeProducer | undefined } {
  const { saveCubeId, isFrozen } = workspace.appliedFilters;
  const isGridFrozen = versions.isCubeFrozen(saveCubeId) ?? isFrozen ?? false;
  return {
    isGridFrozen,
    editableProducer: isGridFrozen ? undefined : producer
  };
}

/**
 * Whether the loaded grid can be saved at all, the guard's Save & apply gate.
 *
 * Gates on the cube the grid was loaded from, not the picker's current choice:
 * the save targets that cube, so a gate on the picker would enable Save for a
 * cube the write never reaches. Skips the in-flight gates, which would toggle
 * the open dialog's button; `saveGrid` refuses a recomputing grid instead.
 */
function canSaveFirstGrid(
  workspace: ForecastWorkspace,
  isGridFrozen: boolean
): boolean {
  return (
    !!workspace.appliedFilters.saveCubeId &&
    !isGridFrozen &&
    isGridSaveable({
      loaded: !!workspace.forecastData,
      isSaving: false,
      isFetching: false,
      calendarView: workspace.tableControls.calendarView
    })
  );
}

/**
 * Whether Save can write the grid now: {@link canSaveFirstGrid}, with the
 * context unchanged since the Apply and nothing in flight.
 *
 * Waits for a recompute in flight — SPEC.md § The copy goes through the
 * mutation queue.
 */
export function canSaveGrid(
  workspace: ForecastWorkspace,
  isContextDirty: boolean,
  isGridFrozen: boolean
): boolean {
  return (
    canSaveFirstGrid(workspace, isGridFrozen) &&
    !isContextDirty &&
    !workspace.isSaving &&
    !workspace.isRecomputing &&
    !workspace.isFetching
  );
}

/** The applied cube's window from the forecast boundary on: the weeks the grid opens. */
function useGridCubeWindow(
  workspace: Pick<ForecastWorkspace, "appliedFilters" | "forecastData">
) {
  const saveCubeWindow = workspace.appliedFilters.saveCubeWindow;
  const forecastWeekFrom = workspace.forecastData?.forecast_week_from;
  return useMemo(
    () => openFromForecastBoundary(saveCubeWindow, forecastWeekFrom),
    [saveCubeWindow, forecastWeekFrom]
  );
}

function useGridRowPredicate(
  workspace: Pick<ForecastWorkspace, "forecastData">
) {
  const holdsSales = workspace.forecastData?.save_cube_holds_sales ?? true;
  const holdsIntake = workspace.forecastData?.save_cube_holds_intake ?? true;
  return useMemo(
    () => cubeHoldsRowPredicate(holdsSales, holdsIntake),
    [holdsSales, holdsIntake]
  );
}

/** What gates the grid's cells: the applied cube's window and its context levels. */
function useGridGates(
  workspace: Pick<ForecastWorkspace, "appliedFilters" | "forecastData">
) {
  return {
    gridCubeWindow: useGridCubeWindow(workspace),
    gridEditableRowPredicate: useGridRowPredicate(workspace)
  };
}

/**
 * Shared state and logic for the forecast and budget main pages.
 * Consolidates context selection, version options, cube IDs, workspace,
 * context sync, grid callbacks, and frozen state.
 *
 * The hook order threads context-options → workspace → sync-check so
 * the dirty check reads the workspace's live ``appliedFilters`` on the
 * same render they change, rather than a useState mirror updated by a
 * useEffect on the next render (which flashed ``ContextSyncWarning``
 * right after Apply).
 */
export function useMainPageState({
  producer,
  saveCubeKey
}: UseMainPageStateParams) {
  const gridRef = useRef<AgGridReact>(null);
  const page = pageForProducer(producer);
  const selection = useCubeSelectionState(page);
  const { selectedContextOptions, setSelectedContextOptions } = selection;
  const versionState = useRetainedState<ForecastVersionSelections>(
    pageScope(page),
    "versionOptions",
    {}
  );
  const [selectedVersionOptions, setSelectedVersionOptions] = versionState;
  const [accumulated, setAccumulated] = useState(false);
  const [graphVisible, setGraphVisible] = useState(false);
  const [eventCalendarVisible, setEventCalendarVisible] = useState(false);

  const versions = useMainPageVersions({
    producer,
    saveCubeKey,
    selectedContextOptions,
    selectedVersionOptions
  });

  useClearStaleVersions(versions, versionState);
  useClearStaleBudgetVersion(versions, setSelectedVersionOptions);
  useOpenForecastOnMainCube(producer, versions, setSelectedVersionOptions);

  const context = useMainPageContext({
    cubeIds: versions.cubeIds,
    selectedContextOptions,
    selectedStoreStatuses: selection.selectedStoreStatuses
  });

  usePruneStaleContextSelections(context, setSelectedContextOptions);
  const contextViews = useContextViewState({ page, selection });

  const workspace = useForecastWorkspace({
    producer,
    cubeIds: versions.cubeIds,
    saveCubeId: versions.saveCubeId,
    saveCubeWindow: versions.saveCubeWindow,
    isFrozen: versions.isFrozen,
    selectedContextOptions,
    resolvedStoreLocations: context.resolvedStoreLocations,
    gridRef
  });

  const views = usePageViews({ producer, gridRef, workspace });

  const contextSync = useMainPageContextSync({
    cubeIds: versions.cubeIds,
    appliedFilters: workspace.appliedFilters,
    context
  });

  const canApply = !workspace.isFetching && !workspace.isSaving;
  const editability = resolveGridEditability(workspace, versions, producer);

  const {
    selectedRows,
    handleSelectionChanged,
    handleCellValueChanged,
    handleRowDataUpdated
  } = useSelectedRowsSync(workspace.handleCellValueChanged);

  return {
    // Keys flagged here are accessed by shared page components via the
    // ``MainPageState`` inferred type — avoid renaming without updating
    // ``TemporalGridPage`` / ``BudgetPage`` render-prop callbacks.
    producer,
    gridRef,
    ...selection,
    selectedVersionOptions,
    setSelectedVersionOptions,
    selectedRows,
    accumulated,
    setAccumulated,
    graphVisible,
    setGraphVisible,
    eventCalendarVisible,
    setEventCalendarVisible,
    ...versions,
    // The grid shows the applied cube, so what gates its cells comes from that
    // cube, not the picker's choice: ``grid*`` and ``editableProducer`` name
    // the applied cube, ``saveCube*`` / ``isFrozen`` (spread above) the picker's.
    ...useGridGates(workspace),
    ...editability,
    storeStatusOptions: context.storeStatusOptions,
    storeStatusLoading: context.storeStatusLoading,
    baseContextOptions: context.baseContextOptions,
    baseLoading: context.baseLoading,
    filteredLoading: context.filteredLoading,
    hasActiveFilters: context.hasActiveFilters,
    mergedFilteredContextOptions: context.mergedFilteredContextOptions,
    relevantStoreStatuses: context.relevantStoreStatuses,
    workspace,
    views,
    contextViews,
    isContextDirty: contextSync.isContextDirty,
    changedFields: contextSync.changedFields,
    canApply,
    canSave: canSaveGrid(
      workspace,
      contextSync.isContextDirty,
      editability.isGridFrozen
    ),
    canSaveFirst: canSaveFirstGrid(workspace, editability.isGridFrozen),
    handleSelectionChanged,
    handleCellValueChanged,
    handleRowDataUpdated,
    handleBatchStart: workspace.handleBatchStart,
    handleBatchEnd: workspace.handleBatchEnd
  };
}
